/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FineTuningPanel - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file FineTuningPanel.tsx
 * @version 1.0.0
 * @description Panel de configuración fine-tuning para endpoints de IA
 * @author WASP Development Team
 * 
 * FUNCIONALIDADES:
 * - Seleccionar endpoint a configurar
 * - Ajustar temperatura (0-2)
 * - Ajustar top_p (0-1)
 * - Ajustar frequency_penalty (0-2)
 * - Ajustar presence_penalty (0-2)
 * - Configurar max_tokens
 * - Preview de configuración
 * - Reset a defaults
 * - Guardar cambios
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Badge,
  ProgressBar,
  InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSlidersH,
  faUndo,
  faSave,
  faInfoCircle,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useAIEndpoints } from '../../../../../hooks/useAIEndpoints';
import { AIEndpoint, AIConfig, DEFAULT_AI_CONFIG } from '../../../../../types/ai.types';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════

const FineTuningPanel: React.FC = () => {
  // ───────────────────────────────────────────────────────────────────────
  // Hooks
  // ───────────────────────────────────────────────────────────────────────
  
  const {
    endpoints,
    isLoading,
    updateEndpoint
  } = useAIEndpoints();

  // ───────────────────────────────────────────────────────────────────────
  // Estado Local
  // ───────────────────────────────────────────────────────────────────────
  
  const [selectedEndpoint, setSelectedEndpoint] = useState<AIEndpoint | null>(null);
  const [config, setConfig] = useState<AIConfig>(DEFAULT_AI_CONFIG);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Filtrar solo endpoints activos
  const activeEndpoints = endpoints.filter(ep => ep.enabled);

  // ───────────────────────────────────────────────────────────────────────
  // Effects
  // ───────────────────────────────────────────────────────────────────────
  
  // Cargar configuración del endpoint seleccionado
  useEffect(() => {
    if (selectedEndpoint) {
      const endpointConfig: AIConfig = {
        temperature: selectedEndpoint.metadata?.temperature ?? DEFAULT_AI_CONFIG.temperature,
        top_p: selectedEndpoint.metadata?.topP ?? DEFAULT_AI_CONFIG.top_p,
        frequency_penalty: selectedEndpoint.metadata?.frequencyPenalty ?? DEFAULT_AI_CONFIG.frequency_penalty,
        presence_penalty: selectedEndpoint.metadata?.presencePenalty ?? DEFAULT_AI_CONFIG.presence_penalty,
        max_tokens: selectedEndpoint.metadata?.maxTokens ?? DEFAULT_AI_CONFIG.max_tokens,
        model: selectedEndpoint.metadata?.model ?? DEFAULT_AI_CONFIG.model
      };
      setConfig(endpointConfig);
      setHasChanges(false);
    }
  }, [selectedEndpoint]);

  // Auto-hide success message
  useEffect(() => {
    if (saveSuccess) {
      const timer = setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess]);

  // ───────────────────────────────────────────────────────────────────────
  // Handlers
  // ───────────────────────────────────────────────────────────────────────
  
  const handleEndpointSelect = (endpointId: string) => {
    const endpoint = endpoints.find(ep => ep.id === endpointId);
    setSelectedEndpoint(endpoint || null);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleConfigChange = (field: keyof AIConfig, value: number | string) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleReset = () => {
    setConfig(DEFAULT_AI_CONFIG);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!selectedEndpoint) return;

    setSaveError(null);

    const result = await updateEndpoint(selectedEndpoint.id, {
      metadata: {
        ...selectedEndpoint.metadata,
        temperature: config.temperature,
        topP: config.top_p,
        frequencyPenalty: config.frequency_penalty,
        presencePenalty: config.presence_penalty,
        maxTokens: config.max_tokens,
        model: config.model
      }
    });

    if (result) {
      setSaveSuccess(true);
      setHasChanges(false);
      setSelectedEndpoint(result);
    } else {
      setSaveError('Error al guardar la configuración');
    }
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render - Slider con preview
  // ───────────────────────────────────────────────────────────────────────
  
  const renderSlider = (
    label: string,
    field: keyof AIConfig,
    min: number,
    max: number,
    step: number,
    description: string,
    colorVariant: 'primary' | 'success' | 'info' | 'warning' = 'primary'
  ) => {
    const value = config[field] as number;
    const percentage = ((value - min) / (max - min)) * 100;

    return (
      <Form.Group className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <Form.Label className="mb-0">
            <strong>{label}</strong>
          </Form.Label>
          <Badge bg={colorVariant} className="px-3">
            {value}
          </Badge>
        </div>
        
        <ProgressBar 
          now={percentage} 
          variant={colorVariant}
          style={{ height: 8, marginBottom: 8 }}
        />
        
        <Form.Range
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => handleConfigChange(field, parseFloat(e.target.value))}
        />
        
        <Form.Text className="text-muted">
          <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
          {description}
        </Form.Text>
      </Form.Group>
    );
  };

  // ───────────────────────────────────────────────────────────────────────
  // Render Principal
  // ───────────────────────────────────────────────────────────────────────
  
  if (isLoading) {
    return (
      <Alert variant="info">
        Cargando endpoints...
      </Alert>
    );
  }

  if (activeEndpoints.length === 0) {
    return (
      <Alert variant="warning">
        <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
        No hay endpoints activos para configurar. Activa al menos un endpoint en la pestaña "Endpoints".
      </Alert>
    );
  }

  return (
    <div>
      {/* Success/Error Messages */}
      {saveSuccess && (
        <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)}>
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
          Configuración guardada exitosamente
        </Alert>
      )}
      
      {saveError && (
        <Alert variant="danger" dismissible onClose={() => setSaveError(null)}>
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {saveError}
        </Alert>
      )}

      {/* Selector de Endpoint */}
      <Row className="mb-4">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h5 className="mb-3">
                <FontAwesomeIcon icon={faSlidersH} className="me-2 text-success" />
                Seleccionar Endpoint
              </h5>
              <Form.Select
                size="lg"
                value={selectedEndpoint?.id || ''}
                onChange={(e) => handleEndpointSelect(e.target.value)}
              >
                <option value="">Selecciona un endpoint...</option>
                {activeEndpoints.map(endpoint => (
                  <option key={endpoint.id} value={endpoint.id}>
                    {endpoint.name} ({endpoint.type})
                  </option>
                ))}
              </Form.Select>
              
              {selectedEndpoint && (
                <div className="mt-3 p-3 bg-light rounded">
                  <small className="text-muted d-block mb-1">
                    <strong>Modelo:</strong> {selectedEndpoint.metadata?.model || 'No especificado'}
                  </small>
                  <small className="text-muted d-block mb-1">
                    <strong>URL:</strong> {selectedEndpoint.url}
                  </small>
                  <small className="text-muted d-block">
                    <strong>Capacidades:</strong> {selectedEndpoint.capabilities.join(', ')}
                  </small>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Panel de Configuración */}
      {selectedEndpoint && (
        <Row>
          <Col lg={8}>
            <Card className="border-0 shadow-sm">
              <Card.Body>
                <h5 className="mb-4">Parámetros de Fine-Tuning</h5>

                {/* Temperature */}
                {renderSlider(
                  'Temperature',
                  'temperature',
                  0,
                  2,
                  0.1,
                  'Controla la aleatoriedad. Valores bajos = más determinista, valores altos = más creativo.',
                  'primary'
                )}

                {/* Top P */}
                {renderSlider(
                  'Top P (Nucleus Sampling)',
                  'top_p',
                  0,
                  1,
                  0.05,
                  'Controla la diversidad del texto. 1.0 = considera todas las opciones, 0.1 = solo las más probables.',
                  'success'
                )}

                {/* Frequency Penalty */}
                {renderSlider(
                  'Frequency Penalty',
                  'frequency_penalty',
                  0,
                  2,
                  0.1,
                  'Penaliza tokens repetidos basándose en su frecuencia. Valores altos = menos repetición.',
                  'info'
                )}

                {/* Presence Penalty */}
                {renderSlider(
                  'Presence Penalty',
                  'presence_penalty',
                  0,
                  2,
                  0.1,
                  'Penaliza tokens ya presentes. Valores altos = más diversidad de temas.',
                  'warning'
                )}

                {/* Max Tokens */}
                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>Max Tokens</strong>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      type="number"
                      min={1}
                      max={32000}
                      step={100}
                      value={config.max_tokens}
                      onChange={(e) => handleConfigChange('max_tokens', parseInt(e.target.value))}
                    />
                    <InputGroup.Text>tokens</InputGroup.Text>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                    Máximo número de tokens en la respuesta (1-32000)
                  </Form.Text>
                </Form.Group>

                {/* Model */}
                <Form.Group className="mb-4">
                  <Form.Label>
                    <strong>Modelo</strong>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={config.model}
                    onChange={(e) => handleConfigChange('model', e.target.value)}
                    placeholder="Ej: gpt-4, claude-3, llama-2"
                  />
                  <Form.Text className="text-muted">
                    <FontAwesomeIcon icon={faInfoCircle} className="me-1" />
                    Identificador del modelo de IA
                  </Form.Text>
                </Form.Group>

                {/* Botones de Acción */}
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={handleReset}
                  >
                    <FontAwesomeIcon icon={faUndo} className="me-2" />
                    Reset a Defaults
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={!hasChanges}
                    className="flex-grow-1"
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Guardar Configuración
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Preview Panel */}
          <Col lg={4}>
            <Card className="border-0 shadow-sm sticky-top" style={{ top: 20 }}>
              <Card.Body>
                <h6 className="mb-3">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  Preview de Configuración
                </h6>

                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Temperature</small>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{config.temperature}</span>
                    <Badge bg={config.temperature < 0.5 ? 'info' : config.temperature < 1 ? 'primary' : 'warning'}>
                      {config.temperature < 0.5 ? 'Determinista' : config.temperature < 1 ? 'Balanceado' : 'Creativo'}
                    </Badge>
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Top P</small>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{config.top_p}</span>
                    <Badge bg={config.top_p < 0.5 ? 'info' : config.top_p < 0.9 ? 'success' : 'primary'}>
                      {config.top_p < 0.5 ? 'Focalizado' : config.top_p < 0.9 ? 'Balanceado' : 'Diverso'}
                    </Badge>
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Frequency Penalty</small>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{config.frequency_penalty}</span>
                    <Badge bg={config.frequency_penalty === 0 ? 'secondary' : config.frequency_penalty < 1 ? 'info' : 'warning'}>
                      {config.frequency_penalty === 0 ? 'Sin penalización' : config.frequency_penalty < 1 ? 'Moderado' : 'Alto'}
                    </Badge>
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Presence Penalty</small>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="fw-bold">{config.presence_penalty}</span>
                    <Badge bg={config.presence_penalty === 0 ? 'secondary' : config.presence_penalty < 1 ? 'info' : 'warning'}>
                      {config.presence_penalty === 0 ? 'Sin penalización' : config.presence_penalty < 1 ? 'Moderado' : 'Alto'}
                    </Badge>
                  </div>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Max Tokens</small>
                  <span className="fw-bold">{config.max_tokens.toLocaleString()}</span>
                </div>

                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Modelo</small>
                  <Badge bg="secondary" className="w-100 text-truncate">
                    {config.model}
                  </Badge>
                </div>

                <hr />

                <Alert variant="info" className="mb-0 small">
                  <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                  <strong>Recomendación:</strong><br />
                  Para respuestas más consistentes, usa temperature bajo (0.3-0.5).
                  Para creatividad, usa valores altos (0.8-1.2).
                </Alert>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default FineTuningPanel;

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════